import { Layout } from 'components/layout'
import { Loader } from 'components/loader'
import { Typography } from 'components/typography'
import { LESSON_NAME, LESSON_TITLE } from 'constants/lessons'
import { PAGES } from 'constants/routes'
import { ENV_BASED_URL_ORIGIN } from 'constants/site'
import { useLoadSettings } from 'hooks/database/use-load-settings'
import { useLoadStats } from 'hooks/database/use-load-stats'
import { useAppSelector } from 'hooks/use-app-selector'
import { useIdList } from 'hooks/use-id-list'
import Link from 'next/link'
import { useAuthUser } from 'next-firebase-auth'
import { useMemo } from 'react'
import { selectLifetimeStats, selectRecentStats, selectStatSlice, selectTestHistory } from 'store/slices/stat'
import {
  selectLowercase,
  selectNum,
  selectPunctuation,
  selectTopAndBottomStats,
  selectUppercaseStat,
} from 'store/slices/stat/stat'
import { Checkbox } from 'ui-kit/checkbox'
import { Divider } from 'ui-kit/divider'
import { formatDate } from 'utils/format-date'
import { formatDuration } from 'utils/format-duration'

import { urlWithSiteOrigin } from '../../utils/url-with-site-origin'
import { StatCard, StatCardProps } from './components/stat-card'
import { StatCardTall } from './components/stat-card/stat-card-tall'
import { TopBottomCategories, TopBottomKeys } from './components/top-bottom-keys/top-bottom-keys'
import { BG_COLOR } from './constants'
import {
  CharacterBasedCheckboxes,
  CharacterBasedFilterContainer,
  CheckboxWrapper,
  Container,
  NoHistoryFound,
  StatCards,
  Table,
  TableWrapper,
} from './stats.styles'

export const Stats = () => {
  useLoadSettings()
  useLoadStats()
  const authUser = useAuthUser()
  const { id, clientInitialized, signOut } = authUser || {}

  const statSlice = useAppSelector(selectStatSlice)
  const testHistory = useAppSelector(selectTestHistory)
  const stats = useAppSelector(selectRecentStats)
  const uppercaseStat = useAppSelector(selectUppercaseStat)
  const lowercaseStat = useAppSelector(selectLowercase)
  const numStat = useAppSelector(selectNum)
  const punctuationStat = useAppSelector(selectPunctuation)

  const { ids: selectedCategories, toggleId } = useIdList<TopBottomCategories>({
    initial: ['uppercase', 'lowercase', 'num', 'punctuation'],
  })

  const topBottom = useAppSelector((state) => selectTopAndBottomStats(state, selectedCategories))

  const avgAccuracy = stats?.avgAccuracy
  const avgWpm = stats?.avgWpm
  const recentTestsCount = stats?.recentTestCount
  const lifetimeStats = useAppSelector(selectLifetimeStats)
  const totalDuration = lifetimeStats?.totalDuration
  const totalTests = lifetimeStats?.testsTaken

  const hasHistory = testHistory.length > 0

  const data = [
    {
      variant: 'Speed',
      icon: (
        <svg
          stroke="#fff"
          fill="#fff"
          strokeWidth="0"
          viewBox="0 0 576 512"
          height="36px"
          width="36px"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M288 32C128.94 32 0 160.94 0 320c0 52.8 14.25 102.26 39.06 144.8 5.61 9.62 16.3 15.2 27.44 15.2h443c11.14 0 21.83-5.58 27.44-15.2C561.75 422.26 576 372.8 576 320c0-159.06-128.94-288-288-288zm0 64c14.71 0 26.58 10.13 30.32 23.65-1.11 2.26-2.64 4.23-3.45 6.67l-9.22 27.67c-5.13 3.49-10.97 6.01-17.64 6.01-17.67 0-32-14.33-32-32S270.33 96 288 96zM96 384c-17.67 0-32-14.33-32-32s14.33-32 32-32 32 14.33 32 32-14.33 32-32 32zm48-160c-17.67 0-32-14.33-32-32s14.33-32 32-32 32 14.33 32 32-14.33 32-32 32zm246.77-72.41l-61.33 184C343.13 347.33 352 364.54 352 384c0 11.72-3.38 22.55-8.88 32H232.88c-5.5-9.45-8.88-20.28-8.88-32 0-33.94 26.5-61.43 59.9-63.59l61.34-184.01c4.17-12.56 17.73-19.45 30.36-15.17 12.57 4.19 19.35 17.79 15.17 30.36zm14.66 57.2l15.52-46.55c3.47-1.29 7.13-2.23 11.05-2.23 17.67 0 32 14.33 32 32s-14.33 32-32 32c-11.38-.01-20.89-6.28-26.57-15.22zM480 384c-17.67 0-32-14.33-32-32s14.33-32 32-32 32 14.33 32 32-14.33 32-32 32z" />
        </svg>
      ),
      avg: avgWpm,
      testNumber: recentTestsCount,
    },
    {
      variant: 'Accuracy',
      icon: (
        <svg
          stroke="#fff"
          fill="#fff"
          strokeWidth="0"
          viewBox="0 0 496 512"
          height="36px"
          width="36px"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M248 8C111.03 8 0 119.03 0 256s111.03 248 248 248 248-111.03 248-248S384.97 8 248 8zm0 432c-101.69 0-184-82.29-184-184 0-101.69 82.29-184 184-184 101.69 0 184 82.29 184 184 0 101.69-82.29 184-184 184zm0-312c-70.69 0-128 57.31-128 128s57.31 128 128 128 128-57.31 128-128-57.31-128-128-128zm0 192c-35.29 0-64-28.71-64-64s28.71-64 64-64 64 28.71 64 64-28.71 64-64 64z" />
        </svg>
      ),
      avg: avgAccuracy,
      testNumber: recentTestsCount,
    },
    {
      variant: 'Duration',
      icon: (
        <svg
          stroke="#fff"
          fill="#fff"
          strokeWidth="0"
          viewBox="0 0 512 512"
          height="36px"
          width="36px"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm57.1 350.1L224.9 294c-3.1-2.3-4.9-5.9-4.9-9.7V116c0-6.6 5.4-12 12-12h48c6.6 0 12 5.4 12 12v137.7l63.5 46.2c5.4 3.9 6.5 11.4 2.6 16.8l-28.2 38.8c-3.9 5.3-11.4 6.5-16.8 2.6z" />
        </svg>
      ),
      avg: totalDuration,
      testNumber: totalTests,
    },
  ]

  const pleaseLogin = useMemo(
    () => (
      <NoHistoryFound>
        Please&nbsp;
        <Link href={`/login/?destination=${encodeURIComponent(`${ENV_BASED_URL_ORIGIN}stats/`)}`} prefetch={false}>
          Login
        </Link>
        &nbsp;to see the stats.
      </NoHistoryFound>
    ),
    []
  )
  const noHistoryFound = useMemo(
    () => (
      <NoHistoryFound>
        No history found. Please&nbsp;
        <Link href={PAGES['/typing-practice/']} prefetch={false}>
          take a test
        </Link>
        .
      </NoHistoryFound>
    ),
    []
  )

  return (
    <Layout
      seoProps={{
        title: 'Stats on recent speed, accuracy, time spent, and history of all tests',
        canonicalUrl: urlWithSiteOrigin('/stats/'),
        pageUrl: urlWithSiteOrigin('/stats/'),
        description:
          'TypingMentor provides effective analysis on your typing performance and exhibits the info-graphic that helps you understand your progress',
      }}
      authLoading={!clientInitialized}
      uid={id}
      signOut={signOut}
    >
      <Container>
        <h1>Stats</h1>
        <Divider noBar wrapperThickness="1.5rem" />
        <StatCards>
          {data.map((stat, index) => (
            <StatCard
              variant={stat.variant as StatCardProps['variant']}
              icon={stat.icon}
              avg={stat.avg}
              testNumber={stat.testNumber}
              key={index}
            />
          ))}
        </StatCards>

        <Divider noBar />
        <Divider noBar />

        <h2>Based on Character Types (Beta)</h2>

        <Divider noBar />

        <StatCards>
          <StatCardTall
            speed={uppercaseStat.averageWpm}
            accuracy={uppercaseStat.averageAccuracy}
            color="#00b2c5"
            topText="ABC"
            title="Uppercase Letters"
          />
          <StatCardTall
            speed={lowercaseStat.averageWpm}
            accuracy={lowercaseStat.averageAccuracy}
            color="#10a5f9"
            topText="abc"
            title="Lowercase Letters"
          />
          <StatCardTall
            speed={numStat.averageWpm}
            accuracy={numStat.averageAccuracy}
            color="#a9834f"
            topText="123"
            title="Numbers"
          />
          <StatCardTall
            speed={punctuationStat.averageWpm}
            accuracy={punctuationStat.averageAccuracy}
            color="#9a1fa5"
            topText="?&!#"
            title="Punctuations"
          />
        </StatCards>

        <Divider noBar />
        <Divider noBar />

        <h2>Based on Characters (Beta)</h2>

        <Divider noBar />

        <CharacterBasedFilterContainer>
          <CharacterBasedCheckboxes>
            {(['uppercase', 'lowercase', 'num', 'punctuation'] as TopBottomCategories[]).map((category) => {
              const isSelected = selectedCategories.includes(category)
              const color = BG_COLOR[category]
              return (
                <CheckboxWrapper color={color} selected={isSelected} onClick={() => toggleId(category)} key={category}>
                  <Checkbox
                    wrapperClass="character-based-category-checkbox"
                    checked={isSelected}
                    handleClick={() => toggleId(category)}
                  />
                  <Divider noBar isVertical wrapperThickness="0.25rem" />
                  {category === 'num' ? 'Numbers' : category === 'punctuation' ? 'Punctuations' : category}
                </CheckboxWrapper>
              )
            })}
          </CharacterBasedCheckboxes>
        </CharacterBasedFilterContainer>

        <Divider noBar />

        {selectedCategories.length === 0 && (
          <Typography as="div" center>
            Please select a category above.
          </Typography>
        )}

        <div style={{ minHeight: '568px', position: 'relative' }}>
          <Loader
            variant="absolute"
            placeholderHeight="568px"
            loading={Boolean(id && clientInitialized && statSlice.loading)}
            name="views-stats-based-on-character"
          >
            <TopBottomKeys {...topBottom} />

            {id &&
              clientInitialized &&
              !statSlice.loading &&
              selectedCategories.length > 0 &&
              topBottom.speed.top.length === 0 && (
                <Typography as="div" center>
                  Not enough data found on the selected category, please take more tests.
                </Typography>
              )}

            {!id && clientInitialized && (
              <>
                <Divider noBar wrapperThickness="8rem" />
                {pleaseLogin}
              </>
            )}

            {!statSlice.loading && !hasHistory && id && (
              <>
                <Divider noBar wrapperThickness="8rem" />
                {noHistoryFound}
              </>
            )}
          </Loader>
        </div>

        <Divider noBar />
        <Divider noBar />

        <h2>Test history</h2>

        <Divider noBar />

        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Lesson</th>
                <th>Duration</th>
                <th>WPM</th>
                <th>Accuracy</th>
              </tr>
            </thead>
            <tbody>
              {/* {statSlice.loading && ( */}
              {!clientInitialized && (
                <tr>
                  <td colSpan={5}>
                    <NoHistoryFound>
                      <Loader name="views-stats-table-td" loading={true}></Loader>
                    </NoHistoryFound>
                  </td>
                </tr>
              )}

              {!statSlice.loading && !hasHistory && id && (
                <tr>
                  <td colSpan={5}>{noHistoryFound}</td>
                </tr>
              )}

              {!id && clientInitialized && (
                <tr>
                  <td colSpan={5}>{pleaseLogin}</td>
                </tr>
              )}

              {id &&
                hasHistory &&
                testHistory?.map((stat, index) => (
                  <tr key={index}>
                    <td>{formatDate(stat.started)}</td>
                    <td>{LESSON_TITLE[stat.name as LESSON_NAME]}</td>
                    <td>{formatDuration(stat.duration)}</td>
                    <td>
                      {stat.wpm.toFixed()}
                      <sup>wpm</sup>
                    </td>
                    <td>{(stat.accuracy * 100).toFixed()}%</td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </TableWrapper>
      </Container>
    </Layout>
  )
}
